import { ParsedNumber } from '../parsed/parsedNumber';
import { z } from 'zod';

export type CustomerAppCouponType = z.infer<typeof CustomerAppCouponType>;
export type CustomerAppCouponValueType = z.infer<typeof CustomerAppCouponValueType>;
export type CustomerAppValueDisplayType = z.infer<typeof CustomerAppCouponDisplayType>;
export type CustomerAppCoupon = z.infer<typeof CustomerAppCoupon>;

export const CustomerAppCouponType = z.enum(['Unknown', 'Coupon', 'Voucher', 'LoyaltyCoupon', 'Deal']);
export const CustomerAppCouponValueType = z.enum(['Unknown', 'Percent', 'Price', 'Absolute', 'Multiplier']);
export const CustomerAppCouponDisplayType = z.enum(['Regular', 'Free', 'Reward', 'Info', 'BuyXPayY', 'BuyAGetB', 'Bundle']);

export const CustomerAppCoupon = z.object({
    /** The ID of the coupon as it is known to the customer app backend. */
    internalId: z.string().nullish(),
    /** The source where the coupon comes from. In most cases it will the the app. Echo it back when activating the coupon. */
    source: z.string().nullish(),
    /** The type of the coupon. */
    couponType: CustomerAppCouponType,
    /** The value type of the coupon. */
    couponValueType: CustomerAppCouponValueType,
    /** A fully qualified URL to an image that represents the coupon. */
    imageUrl: z.string().nullish(),
    /** The main title of the coupon. */
    title: z.string().nullish(),
    /** The subtitle of the coupon. */
    subTitle: z.string().nullish(),
    /** A long description of the coupon. This can be used to provide more information about the coupon. */
    description: z.string().nullish(),
    /** In case a coupon has a validity, this is the date from which the coupon is valid. */
    validFrom: z.coerce.date().nullish(),
    /** In case a coupon has a validity, this is the date until which the coupon is valid. */
    validTo: z.coerce.date().nullish(),
    /** The minimum total amount that a customer has to spend in order to be able to use the coupon. */
    minTotal: z.number().nullish(),
    /** Specifies whether the customer can proactivly activate the coupon. */
    isUserActivatable: z.boolean(),
    /** Specifies whether the coupon is activated within the customer app. */
    isActivated: z.boolean(),
    /** Specifies whether the coupon is new to the customer. */
    isNew: z.boolean(),
    /** The value of the coupon as a decimal value. */
    value: ParsedNumber.nullish(),
    /** The value of the coupon as a text as it typically comes from the backend. */
    valueText: z.string().nullish(),
    /** The symbol that should be used together with the value to be displayed. */
    symbol: z.string().nullish(),
    /** A sortable property (if available) that can be used to sort the coupons. The lower the number, the higher the priority. */
    order: z.number().nullish(),
    /** The display type of the coupon value. This is typically used to display special labels like "free" or "premium". */
    couponDisplayType: CustomerAppCouponDisplayType,
    /** Optional terms and conditions text that can be displayed together with the coupon. */
    optionalConditionsText: z.string().nullish(),
    /** Optional additional information text that can be displayed together with the coupon. */
    optionalAdditionalInformationText: z.string().nullish(),
    /** Specifies whether the coupon is currently in the process of being activated. */
    activationPending: z.boolean().nullish(),
});
